import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MUTATION_KEYS, QUERY_KEYS } from 'constants/common';
import { SURVEY_TYPES } from 'constants/surveys';
import { SurveyService } from 'services/surveys';
import {
  type SurveyType,
  type Survey,
  type SurveyAnswer,
  type SurveyResponse,
} from 'types/surveys';

interface UseSaveSurveyResponseOptions {
  surveyType: SurveyType;
  onSuccess?: (data: SurveyResponse) => void;
  successMessageKey?: string;
}

export const useSaveSurveyResponse = (
  survey?: Survey,
  options?: UseSaveSurveyResponseOptions
): UseMutationResult<AxiosResponse<SurveyResponse>, Error, SurveyAnswer> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const surveyType = options?.surveyType || SURVEY_TYPES.DAILY;

  return useMutation({
    mutationKey: [MUTATION_KEYS.SAVE_DAILY_TRACKING, surveyType],
    mutationFn: async ({ questionId, optionIds, requiredTextValue }: SurveyAnswer) =>
      SurveyService.saveSurveyResponse(survey?.id ?? '', {
        questionId,
        optionIds,
        requiredTextValue,
      }),
    onSuccess: ({ data }) => {
      const { completed } = data;
      const surveyId = survey?.id;

      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_SURVEYS, surveyType] });

      if (surveyId) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_SURVEY_RESPONSES, surveyId] });

        const queryKey = [QUERY_KEYS.FETCH_SURVEY_QUESTIONS, surveyId];

        if (completed) {
          queryClient.removeQueries({ queryKey });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.FETCH_DAILY_TRACKING_HISTORY, surveyId],
          });
        }

        if (!completed) {
          queryClient.invalidateQueries({ queryKey });
        }
      }

      if (surveyType !== SURVEY_TYPES.TEST) {
        toast.success(t(options?.successMessageKey ?? 'Survey.success'));
      }

      options?.onSuccess?.(data);
    },
  });
};
